import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Users, ShoppingCart, FilePlus, Truck, FileText, Settings, LogOut, Factory, HardHat, Banknote, ClipboardList, IndianRupee, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const allNavItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'supervisor', 'data_entry'] },
  { path: '/buyers', label: 'Buyer Master', icon: Users, roles: ['admin', 'manager', 'supervisor'] },
  { path: '/po/list', label: 'Purchase Orders', icon: ShoppingCart, roles: ['admin', 'manager', 'supervisor'] },
  { path: '/po/new', label: 'New PO', icon: FilePlus, roles: ['admin', 'manager', 'supervisor'] },
  { path: '/dispatch/new', label: 'Dispatch Entry', icon: Truck, roles: ['admin', 'manager', 'supervisor'] },
  { path: '/job-cards', label: 'Job Cards', icon: ClipboardList, roles: ['admin', 'manager', 'supervisor', 'data_entry'] },
  { path: '/production', label: 'Production', icon: Factory, roles: ['admin', 'manager', 'supervisor', 'data_entry'] },
  { path: '/job-workers', label: 'Job Workers', icon: HardHat, roles: ['admin', 'manager', 'supervisor', 'data_entry'] },
  { path: '/wages-master', label: 'Wages Master', icon: IndianRupee, roles: ['admin', 'manager', 'data_entry'] },
  { path: '/wages', label: 'Wages & Penalties', icon: Banknote, roles: ['admin', 'manager', 'data_entry'] },
  { path: '/reports', label: 'Reports', icon: FileText, roles: ['admin', 'manager', 'supervisor', 'data_entry'] },
  { path: '/users', label: 'User Management', icon: Settings, roles: ['admin'] },
];

const NavPanel = ({ navItems, pathname, user, userRole, logout, onClose, testPrefix }) => (
  <>
    <div className="p-4 border-b border-zinc-800 flex items-start justify-between">
      <div>
        <h2 className="text-base font-bold tracking-tight">RP India</h2>
        <p className="text-xs text-zinc-400 mt-0.5">{user?.name || 'User'}</p>
        <span data-testid={`${testPrefix}user-role-badge`} className="inline-block mt-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-zinc-700 text-zinc-300 uppercase tracking-wider">
          {userRole}
        </span>
      </div>
      {onClose && (
        <button onClick={onClose} className="md:hidden p-1 text-zinc-400 hover:text-white" aria-label="Close menu" data-testid="mobile-menu-close">
          <X className="w-5 h-5" />
        </button>
      )}
    </div>

    <nav className="flex-1 py-3 overflow-y-auto">
      {navItems.map(item => {
        const active = pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onClose}
            data-testid={`${testPrefix}nav-${item.path.replace(/\//g, '-').slice(1)}`}
            className={`flex items-center gap-2.5 px-4 py-2.5 md:py-2 text-sm transition-colors ${
              active ? 'bg-zinc-800 text-white font-medium border-l-2 border-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
            }`}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </Link>
        );
      })}
    </nav>

    <div className="p-3 border-t border-zinc-800">
      <Button
        data-testid={`${testPrefix}logout-btn`}
        variant="ghost"
        className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800"
        onClick={logout}
      >
        <LogOut className="w-4 h-4 mr-2" /> Logout
      </Button>
    </div>
  </>
);

export default function Sidebar() {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const userRole = user?.role || 'supervisor';
  const navItems = allNavItems.filter(item => item.roles.includes(userRole));

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const activeLabel = navItems.find(i => i.path === location.pathname)?.label || 'RP India';
  const panelProps = { navItems, pathname: location.pathname, user, userRole, logout };

  return (
    <>
      {/* Desktop sidebar */}
      <div data-testid="sidebar" className="hidden md:flex w-56 bg-zinc-900 text-white flex-col h-screen fixed z-30">
        <NavPanel {...panelProps} testPrefix="" />
      </div>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 h-14 z-40 bg-zinc-900 text-white flex items-center gap-3 px-3 border-b border-zinc-800" data-testid="mobile-topbar">
        <button onClick={() => setOpen(true)} className="p-2 -ml-1 text-zinc-200 hover:text-white" aria-label="Open menu" data-testid="mobile-menu-open">
          <Menu className="w-6 h-6" />
        </button>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-zinc-400 leading-none">RP India</p>
          <h1 className="text-sm font-bold truncate leading-tight" data-testid="mobile-page-title">{activeLabel}</h1>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`md:hidden fixed inset-0 z-50 bg-black/60 transition-opacity duration-200 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setOpen(false)}
        data-testid="mobile-drawer-overlay"
      />
      <aside
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-zinc-900 text-white flex flex-col transition-transform duration-200 ease-out ${open ? 'translate-x-0' : '-translate-x-full'}`}
        data-testid="mobile-drawer"
        aria-hidden={!open}
      >
        <NavPanel {...panelProps} onClose={() => setOpen(false)} testPrefix="mobile-" />
      </aside>
    </>
  );
}
